// "Booking setup" block shown above the seat picker. Captures the time
// window + party size the customer wants so we can preflight capacity +
// disable already-taken seats.
//
// UX varies by service type AND by the tenant-set
// services.booking_panel_layout (PR R, migration 20260527000020):
//   - accommodation:     check-in + check-out date (overrides layout)
//   - events:            read-only; window derives from the chosen event unit
//   - datetime:          datetime-local input + duration + party (DEFAULT)
//   - calendar_first:    full month grid → time chips for the picked date
//   - time_list:         date input + vertical time-chip list
//   - single_slot:       date input + party only; time auto-resolved
//   - scheduled_sessions: dated service units rendered as day-grouped
//                         tabs; picking one pins the window
//
// All layouts produce the same {starts_at, ends_at, party_size} —
// downstream cart, holds, and capacity stay identical.

import type React from "react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { WidgetService, WidgetUnit } from "../types";
import { fieldHidden, fieldLabel } from "../state/field-labels";
import { isOptionLed } from "../state/option-led";
import { useServiceBookingWindow } from "../state/booking-window";
import {
  type BookingWindow,
  addMinutes,
  bookingDurationMinutes,
  durationOptionsFor,
  fromLocalInput,
  toLocalInput,
} from "../state/booking";
import {
  type BusyInterval,
  findOverlap,
  formatSuggestion,
  suggestNextFreeSlot,
} from "../state/conflicts";
import {
  hoursPillText,
  inputBoundsForDate,
  isWithinServiceHours,
  openWindowsMsForDate,
  windowsForDate,
  type ServiceHoursRow,
} from "../state/service-hours";
import { setTripDateFromIso, tripDateAsDate, tripDateIsFuture } from "../state/trip-date";
import { WidgetLayer } from "./WidgetLayer";

export function BookingSetup({
  service,
  window,
  selectedEventUnit,
  onChange,
  busyIntervals = [],
  serviceHours = [],
  capacityRemainingText = null,
  showStatusPills = true,
  seatingField = null,
  seatingPreview = null,
  hidePartyStepper = false,
  fixedNights = null,
  minNights = null,
  defaultPartySize = null,
  allowedDaysOfWeek = null,
  ctaSlot = null,
  onSeed,
}: {
  service: WidgetService;
  window: BookingWindow | null;
  selectedEventUnit: WidgetUnit | null;
  onChange: (w: BookingWindow) => void;
  /**
   * Accommodation only: when a combo/package includes a fixed number of nights
   * (e.g. "pay for 2, stay for 3"), the stay is auto-set to N nights and the
   * check-out date follows the check-in instead of being freely picked.
   */
  fixedNights?: number | null;
  /**
   * Accommodation only: the SHORTEST stay a package will accept, without
   * pinning the length. "Stay & Save 25%, minimum 2 nights" means two nights
   * or more — treating that as a fixed length blocks the three-night stay the
   * offer exists to sell.
   */
  minNights?: number | null;
  /**
   * Combo/package requirement: seed the party stepper to this size instead of
   * the generic default of 2, so a package that specifies "1 guest" (or "4
   * guests") opens already satisfying its own condition. Guest can still change
   * it. Ignored when null / not positive.
   */
  defaultPartySize?: number | null;
  /**
   * Combo/package requirement: the days-of-week the package is valid on
   * (JS getDay(): 0=Sun..6=Sat — the same convention the combo eligibility RPC
   * uses via Postgres extract(dow)). When set, accommodation seeds its check-in
   * on the next permitted day so the package doesn't open on an invalid day.
   */
  allowedDaysOfWeek?: readonly number[] | null;
  /**
   * Time windows already held in the cart for OTHER services. When the
   * customer's current pick overlaps one of these, we surface an
   * inline soft-warn above the WHEN row with a "Try X instead" link
   * (next non-conflicting same-day slot). Empty array disables the
   * cart-aware behaviour entirely. See state/conflicts.ts.
   */
  busyIntervals?: readonly BusyInterval[];
  /**
   * Operating hours rows for this service (one row per dow + window).
   * Used by DatetimeSetup to constrain the datetime-local input's
   * min/max and render an "Open today 12:00–22:00" pill above it.
   * Empty array (or no rows for the picked day) = no constraint —
   * the server-side trigger is the source of truth either way.
   * See state/service-hours.ts.
   */
  serviceHours?: readonly ServiceHoursRow[];
  /**
   * Optional "N left in area · N left in service" text from the
   * parent's capacity probe. Rendered inline with the hours pill so
   * the customer sees availability + capacity in one row. Parent
   * decides when capacity is known; when null, only the hours pill
   * shows.
   */
  capacityRemainingText?: string | null;
  /**
   * Render the hours/capacity pills inline (below the fields). ServicePanel
   * turns this off — it shows the same pills in the service hero instead —
   * while CombosPanel keeps the inline default.
   */
  showStatusPills?: boolean;
  /**
   * Seating-area <select> injected into the WHEN/DURATION grid (datetime
   * layout only) so the picker reads as part of the booking row. Null when
   * the service has no seating areas or uses a non-datetime layout.
   */
  seatingField?: ReactNode;
  /**
   * Hero image of the selected area + thumbnail strip, rendered directly
   * below the grid. Pairs with seatingField.
   */
  seatingPreview?: ReactNode;
  /**
   * Hide the shared Party stepper in EVERY layout. Set on a special date,
   * whose own picker (party chips, or the per-menu split) owns the party: a
   * second stepper would let a party through the date does not offer, or
   * disagree with the split.
   */
  hidePartyStepper?: boolean;
  /**
   * Booking CTA rendered as the LAST cell of the setup grid (after the party
   * stepper) so the reservation's action lives IN the date/party row instead
   * of a separate bar the guest has to find. Same injection pattern as
   * seatingField. Applies to the layout variants sharing .vt-setup-grid;
   * calendar_first / scheduled_sessions have no such row and ignore it.
   */
  ctaSlot?: ReactNode;
  /** Fired on every (re)seed with the auto-seeded start, so the parent can tell
   *  whether the guest is still on the default calendar day. */
  onSeed?: (startsAt: string) => void;
}) {
  // Seed sensible defaults once. A combo/package can pre-set the headcount
  // (defaultPartySize) so the form opens already meeting the package's own
  // party-size condition.
  const seedParty =
    defaultPartySize != null && defaultPartySize > 0 ? defaultPartySize : 2;

  // Booking horizon: cap the date pickers at "today + max_advance_days" so a
  // guest can't pick beyond the window the venue takes bookings for. The hold +
  // WhatsApp bot already reject dates past it (with a clear message), but this
  // stops the guest getting that far only to be turned away.
  // Read through the shared per-service cache (state/booking-window.ts).
  const bookingWindow = useServiceBookingWindow(service.id);
  const horizonDays =
    bookingWindow.maxAdvanceDays != null && bookingWindow.maxAdvanceDays > 0
      ? bookingWindow.maxAdvanceDays
      : null;
  // Advance-notice rule (min_lead_hours): known upfront, so the guest reads
  // "book at least N hours ahead" at the TOP of the panel instead of
  // discovering it from a rejection below the fold.
  const minLeadHours =
    bookingWindow.minLeadHours != null && bookingWindow.minLeadHours > 0
      ? bookingWindow.minLeadHours
      : null;
  // Upcoming closures for the notice strip — the guest message's proactive
  // stage, not only the refusal copy.
  const closures = bookingWindow.closures;
  const horizonMax = useMemo(() => {
    if (horizonDays == null) return null;
    const d = new Date();
    d.setDate(d.getDate() + horizonDays);
    const pad = (n: number) => String(n).padStart(2, "0");
    const dateMax = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    return { dateMax, whenMax: `${dateMax}T23:59` };
  }, [horizonDays]);
  // The last starts_at WE auto-seeded — lets a late serviceHours load correct
  // the default without overwriting a time the guest picked in between.
  const lastSeedRef = useRef<string | null>(null);
  useEffect(() => {
    // Seed the booking window once, then re-seed ONLY if the guest hasn't moved
    // the time off our last auto-seed — so a late serviceHours load can correct
    // the default without clobbering a manual choice.
    if (
      window != null &&
      lastSeedRef.current != null &&
      window.starts_at !== lastSeedRef.current
    ) {
      return;
    }
    const seed = (starts_at: string, ends_at: string) => {
      lastSeedRef.current = starts_at;
      // Tell the parent the default we landed on, so it can gate the
      // "did you mean this date?" confirm on the seeded calendar day.
      onSeed?.(starts_at);
      onChange({
        starts_at,
        ends_at,
        // Preserve a party the guest already set (a re-seed on hours-load
        // must not reset it); use the default only on the first seed.
        party_size: window?.party_size ?? seedParty,
      });
    };

    if (service.service_type === "events") {
      if (selectedEventUnit?.starts_at && selectedEventUnit?.ends_at) {
        seed(selectedEventUnit.starts_at, selectedEventUnit.ends_at);
      }
      return;
    }
    // Scheduled-sessions seed: pick the first upcoming dated unit.
    // Without this the window stays null and the panel renders empty
    // until the customer taps a tab — bad first-paint UX. When the
    // service has no dated units configured at all we fall through
    // to the generic seed so window is non-null; the layout itself
    // surfaces the empty-state to the tenant.
    if (service.booking_panel_layout === "scheduled_sessions") {
      const upcoming = firstUpcomingDatedUnit(service.units);
      if (upcoming?.starts_at && upcoming.ends_at) {
        seed(upcoming.starts_at, upcoming.ends_at);
        return;
      }
    }
    // Accommodation seeds a sensible 1-night stay at proper check-in / check-out
    // times (today 15:00 -> tomorrow 11:00) rather than the current clock time,
    // which used to seed a check-in of e.g. 23:30. AccommodationSetup then edits
    // the two dates; the 15:00/11:00 times mirror its own pickers.
    if (service.service_type === "accommodation") {
      // A day-restricted package seeds its check-in on the next permitted day
      // so it doesn't open on a day the package can't be booked. A future trip
      // date (picked on another tab / already in the cart) seeds check-in on
      // the guest's date instead of today.
      const seedBase = tripDateIsFuture() ? tripDateAsDate()! : new Date();
      const checkIn = firstAllowedDay(seedBase, allowedDaysOfWeek);
      checkIn.setHours(15, 0, 0, 0);
      const checkOut = new Date(checkIn);
      // Open at the package's floor so the offer is eligible on arrival; a
      // guest can still lengthen the stay.
      checkOut.setDate(
        checkOut.getDate() + Math.max(1, fixedNights ?? minNights ?? 1),
      );
      checkOut.setHours(11, 0, 0, 0);
      seed(checkIn.toISOString(), checkOut.toISOString());
      return;
    }
    // Generic services: seed WITHIN the service's opening hours when known,
    // instead of forcing an evening slot — a lunch / tasting service (e.g.
    // 11:00–15:00) otherwise defaulted to 18:00, outside hours, and 400'd every
    // create_booking_hold so nothing could be added to the cart.
    const now = new Date();
    let next = roundUpToHalfHour(now);
    // A future trip date (picked on another tab / already in the cart) wins
    // over "today": seed on that day at its first opening — 18:00 when the
    // service's hours are unknown — instead of resetting the guest to today.
    const tripBase = tripDateIsFuture() ? tripDateAsDate() : null;
    if (tripBase) {
      const tripWindows = serviceHours.length
        ? openWindowsMsForDate(serviceHours, tripBase)
        : [];
      if (tripWindows.length > 0) {
        next = new Date(tripWindows[0]!.start);
      } else {
        tripBase.setHours(18, 0, 0, 0);
        next = tripBase;
      }
      const starts = next.toISOString();
      const mins = bookingDurationMinutes(service);
      seed(starts, addMinutes(starts, mins));
      return;
    }
    const todays = serviceHours.length
      ? openWindowsMsForDate(serviceHours, next)
      : [];
    if (todays.length > 0) {
      const nowMs = next.getTime();
      // A window we can still start in today (30-min headroom); else tomorrow's
      // first opening; else snap to today's first opening.
      const openNow = todays.find((w) => w.end - 30 * 60_000 > nowMs);
      if (openNow) {
        next = new Date(Math.max(nowMs, openNow.start));
      } else {
        const tmw = new Date(next);
        tmw.setDate(tmw.getDate() + 1);
        tmw.setHours(0, 0, 0, 0);
        const tw = openWindowsMsForDate(serviceHours, tmw);
        next = new Date((tw[0] ?? todays[0])!.start);
      }
    } else {
      // Unknown hours (fail-open, like the server) — sensible evening default.
      next.setHours(Math.max(next.getHours(), 18), next.getMinutes(), 0, 0);
    }
    const starts = next.toISOString();
    const mins = bookingDurationMinutes(service);
    seed(starts, addMinutes(starts, mins));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service.id, selectedEventUnit?.id, serviceHours]);

  // User-driven window edits (vs. our auto-seeds, which route through seed()
  // above) pin the shared trip date, so the other tabs open on the guest's
  // date. Auto-seeded values are recognised via lastSeedRef and skipped.
  const onUserChange = (w: BookingWindow) => {
    if (w.starts_at && w.starts_at !== lastSeedRef.current) {
      // The guest moved the date/time off our auto-seed — pin the shared trip
      // date so the other tabs open on their chosen date.
      setTripDateFromIso(w.starts_at);
    }
    onChange(w);
  };

  if (!window) {
    return null;
  }
  // Top-of-panel advance-notice banner: the rule the guest must plan around,
  // stated before they invest in picking dates — not after a rejection.
  const leadBanner =
    minLeadHours != null ? (
      <div className="vt-lead-notice" role="note">
        Bookings need to be made at least {minLeadHours} hour
        {minLeadHours === 1 ? "" : "s"} in advance.
      </div>
    ) : null;

  // "Closed 24 Aug – 8 Oct — we're closed for renovations." Announced where
  // the notices live, before the guest invests in picking dates. The calendar
  // still greys the days; this is the sentence that explains them.
  const fmtDay = (d?: string) => {
    if (!d) return "";
    const dt = new Date(`${d}T00:00:00`);
    if (Number.isNaN(dt.getTime())) return d;
    const sameYear = dt.getFullYear() === new Date().getFullYear();
    return dt.toLocaleDateString("en-ZA", {
      day: "numeric",
      month: "short",
      ...(sameYear ? {} : { year: "numeric" }),
    });
  };
  const closureBanner =
    closures.length > 0 ? (
      <div className="vt-lead-notice" role="note">
        {closures.slice(0, 2).map((c, i) => {
          const span =
            c.starts_on === c.ends_on
              ? fmtDay(c.starts_on)
              : `${fmtDay(c.starts_on)} – ${fmtDay(c.ends_on)}`;
          const msg = c.guest_message?.trim();
          return (
            <span key={i} className="vt-lead-notice-line">
              Closed {span}
              {msg ? ` — ${msg}` : "."}
            </span>
          );
        })}
      </div>
    ) : null;
  const noticeBanners = (
    <>
      {leadBanner}
      {closureBanner}
    </>
  );


  if (service.service_type === "events") {
    return (
      <>
        {noticeBanners}
      <EventsSetup
        service={service}
        window={window}
        selectedEventUnit={selectedEventUnit}
        onChange={onUserChange}
        busyIntervals={busyIntervals}
        ctaSlot={ctaSlot}
        hidePartyStepper={hidePartyStepper}
      />
      </>
    );
  }

  if (service.service_type === "accommodation") {
    return (
      <>
        {noticeBanners}
      <AccommodationSetup
        window={window}
        onChange={onUserChange}
        hidePartyStepper={hidePartyStepper}
        fixedNights={fixedNights}
        minNights={minNights}
        ctaSlot={ctaSlot}
        maxDate={horizonMax?.dateMax}
        serviceId={service.id}
      />
      </>
    );
  }

  const layout = service.booking_panel_layout ?? "datetime";

  if (layout === "single_slot") {
    return (
      <>
        {noticeBanners}
      <SingleSlotSetup
        service={service}
        window={window}
        onChange={onUserChange}
        serviceHours={serviceHours}
        ctaSlot={ctaSlot}
        hidePartyStepper={hidePartyStepper}
      />
      </>
    );
  }

  if (layout === "time_list") {
    return (
      <>
        {noticeBanners}
      <TimeListSetup
        service={service}
        window={window}
        onChange={onUserChange}
        busyIntervals={busyIntervals}
        serviceHours={serviceHours}
        ctaSlot={ctaSlot}
        hidePartyStepper={hidePartyStepper}
      />
      </>
    );
  }

  if (layout === "calendar_first") {
    return (
      <CalendarFirstSetup
        service={service}
        window={window}
        onChange={onUserChange}
        busyIntervals={busyIntervals}
        serviceHours={serviceHours}
        hidePartyStepper={hidePartyStepper}
      />
    );
  }

  if (layout === "scheduled_sessions") {
    return (
      <ScheduledSessionsSetup
        service={service}
        window={window}
        onChange={onUserChange}
        busyIntervals={busyIntervals}
        hidePartyStepper={hidePartyStepper}
      />
    );
  }

  return (
    <>
      {noticeBanners}
    <DatetimeSetup
      service={service}
      window={window}
      onChange={onUserChange}
      busyIntervals={busyIntervals}
      serviceHours={serviceHours}
      capacityRemainingText={capacityRemainingText}
      showStatusPills={showStatusPills}
      seatingField={seatingField}
      seatingPreview={seatingPreview}
      hidePartyStepper={hidePartyStepper}
      ctaSlot={ctaSlot}
      maxWhen={horizonMax?.whenMax}
    />
    </>
  );
}

// ─── Conflict banner (shared by Datetime + Events) ───────────────────────
function ConflictBanner({
  window,
  busyIntervals,
  onAccept,
  serviceHours,
}: {
  window: BookingWindow;
  busyIntervals: readonly BusyInterval[];
  onAccept: (newStartMs: number) => void;
  serviceHours?: readonly ServiceHoursRow[];
}) {
  const startMs = Date.parse(window.starts_at);
  const endMs = Date.parse(window.ends_at);
  const collision = useMemo(() => {
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return null;
    return findOverlap(busyIntervals, startMs, endMs);
  }, [busyIntervals, startMs, endMs]);
  const suggestion = useMemo(() => {
    if (!collision) return null;
    // Constrain the suggested alternative to the service's operating hours so
    // we never propose a time the server would then reject as out-of-hours.
    const openWindows =
      serviceHours && serviceHours.length > 0
        ? openWindowsMsForDate(serviceHours, new Date(startMs))
        : undefined;
    return suggestNextFreeSlot(busyIntervals, startMs, endMs, { openWindows });
  }, [collision, busyIntervals, startMs, endMs, serviceHours]);
  if (!collision) return null;
  return (
    <div className="vt-conflict-banner" role="status">
      <span className="vt-conflict-banner-text">
        ⚠ Overlaps with <strong>{collision.description}</strong> already in your booking.
      </span>
      {suggestion != null && suggestion !== startMs && (
        <button
          type="button"
          className="vt-conflict-banner-suggest"
          onClick={() => onAccept(suggestion)}
        >
          Try {formatSuggestion(suggestion)} →
        </button>
      )}
    </div>
  );
}

// ─── Datetime (dining/spa/tasting/picnic/tour/custom) ─────────────────────

function DatetimeSetup({
  service,
  window,
  onChange,
  busyIntervals,
  serviceHours,
  capacityRemainingText,
  showStatusPills = true,
  seatingField,
  seatingPreview,
  hidePartyStepper = false,
  ctaSlot,
  maxWhen,
}: {
  service: WidgetService;
  window: BookingWindow;
  onChange: (w: BookingWindow) => void;
  busyIntervals: readonly BusyInterval[];
  serviceHours: readonly ServiceHoursRow[];
  capacityRemainingText: string | null;
  showStatusPills?: boolean;
  seatingField?: ReactNode;
  seatingPreview?: ReactNode;
  hidePartyStepper?: boolean;
  ctaSlot?: ReactNode;
  /** datetime-local upper bound from the service's booking horizon, if any. */
  maxWhen?: string;
}) {
  const duration = minutesBetween(window.starts_at, window.ends_at);
  // Option-led services: the OPTION carries its length, so a free-choice
  // duration here would silently resize (and mis-price) the booking.
  const hideDuration = fieldHidden(service, "duration") || isOptionLed(service);

  // Operating hours: pill text + datetime-local input bounds. We base
  // both on the local calendar date of the currently-picked WHEN
  // value, so if the customer scrolls into tomorrow the pill + bounds
  // update too.
  const pickedDate = useMemo(
    () => new Date(window.starts_at),
    [window.starts_at],
  );
  const hoursPill = useMemo(
    () => hoursPillText(serviceHours, pickedDate),
    [serviceHours, pickedDate],
  );
  const hoursBounds = useMemo(
    () => inputBoundsForDate(serviceHours, pickedDate),
    [serviceHours, pickedDate],
  );
  // Live out-of-hours feedback: flag the picked window on change rather than
  // only rejecting it at submit. Fail-open when no hours are configured.
  const outOfHours = useMemo(
    () =>
      serviceHours.length > 0 &&
      !isWithinServiceHours(
        serviceHours,
        new Date(window.starts_at),
        new Date(window.ends_at),
      ),
    [serviceHours, window.starts_at, window.ends_at],
  );

  function setStarts(local: string) {
    const starts = fromLocalInput(local);
    onChange({
      ...window,
      starts_at: starts,
      ends_at: addMinutes(starts, duration),
    });
  }

  function setStartsMs(ms: number) {
    const starts = new Date(ms).toISOString();
    onChange({
      ...window,
      starts_at: starts,
      ends_at: addMinutes(starts, duration),
    });
  }

  function setDuration(minutes: number) {
    onChange({
      ...window,
      ends_at: addMinutes(window.starts_at, Math.max(30, minutes)),
    });
  }

  return (
    <div className="vt-setup">
      <ConflictBanner
        window={window}
        busyIntervals={busyIntervals}
        onAccept={setStartsMs}
        serviceHours={serviceHours}
      />
      <ClosedDayBanner
        serviceId={service.id}
        dateIso={toLocalInput(window.starts_at).slice(0, 10)}
      />
      {outOfHours && (
        <div className="vt-conflict-banner" role="alert">
          <span className="vt-conflict-banner-text">
            ⚠ That time is outside opening hours
            {hoursPill ? ` — ${hoursPill.label}` : ""}. Pick a time within the
            window.
          </span>
        </div>
      )}
      <div
        className={`vt-setup-grid${seatingField ? " vt-setup-grid--with-seating" : ""}${ctaSlot ? " vt-setup-grid--with-cta" : ""}${hideDuration ? " vt-setup-grid--no-duration" : ""}`}
      >
        <DayNavField
          label={fieldLabel(service, "when", "When")}
          type="datetime-local"
          value={toLocalInput(window.starts_at)}
          inputMin={hoursBounds?.min}
          inputMax={maxWhen ?? hoursBounds?.max}
          minDay={todayDateOnly()}
          maxDay={maxWhen ? maxWhen.slice(0, 10) : undefined}
          serviceId={service.id}
          onChange={setStarts}
        />
        {seatingField}
        {/* Tenants can hide the picker (config.hidden_fields: ["duration"]) —
            e.g. when every sitting is simply "a table for the evening". The
            window then keeps the service-type default duration. */}
        {!hideDuration && (
          <div className="vt-setup-field">
            <label>{fieldLabel(service, "duration", "Duration")}</label>
            <select
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
            >
              {durationOptionsFor(
                service,
                standardDurationsFor(service.service_type),
              ).map((m) => (
                <option key={m} value={m}>
                  {m >= 60
                    ? `${Math.floor(m / 60)}h${m % 60 ? ` ${m % 60}m` : ""}`
                    : `${m} min`}
                </option>
              ))}
            </select>
          </div>
        )}
        {!hidePartyStepper && (
          <PartyStepper window={window} onChange={onChange} service={service} />
        )}
        {ctaSlot && <div className="vt-setup-cta">{ctaSlot}</div>}
      </div>
      {seatingPreview}
      {/* Hours + remaining-capacity pills sit BELOW the fields + seating
          previews (was above the grid) — they're status context, not inputs,
          so the guest's eye lands on the fields first. */}
      {showStatusPills && (hoursPill || capacityRemainingText) && (
        <div className="vt-hours-row">
          {hoursPill && (
            <div
              className={`vt-hours-pill${hoursPill.isOpen ? "" : " vt-hours-pill-closed"}`}
              role="status"
            >
              <span className="vt-hours-pill-dot" aria-hidden="true" />
              {hoursPill.label}
            </div>
          )}
          {capacityRemainingText && (
            <div className="vt-capacity-hint vt-capacity-hint-inline">
              {capacityRemainingText}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Single slot (date + party, time auto-resolved from hours) ───────────

function SingleSlotSetup({
  service,
  window,
  onChange,
  serviceHours,
  ctaSlot,
  hidePartyStepper = false,
}: {
  service: WidgetService;
  window: BookingWindow;
  onChange: (w: BookingWindow) => void;
  serviceHours: readonly ServiceHoursRow[];
  ctaSlot?: ReactNode;
  /** A special date's own picker owns the party (see DatetimeSetup). */
  hidePartyStepper?: boolean;
}) {
  const duration = minutesBetween(window.starts_at, window.ends_at);
  const date = useMemo(
    () => toDateOnly(window.starts_at),
    [window.starts_at],
  );
  const pickedDate = useMemo(
    () => new Date(window.starts_at),
    [window.starts_at],
  );
  // Today's open window — the time we lock the slot to. Null when the
  // service has no hours configured, or the picked day has no open
  // window. We refuse to invent a fallback time — without configured
  // hours there's no honest "the daily slot is at X" to display.
  const todayWindow = useMemo(
    () => windowsForDate(serviceHours, pickedDate)[0] ?? null,
    [serviceHours, pickedDate],
  );
  const hoursUnconfigured = serviceHours.length === 0;

  function setDate(v: string) {
    const picked = new Date(`${v}T00:00:00`);
    const windows = windowsForDate(serviceHours, picked);
    // Anchor to the new date's open time when we have it; otherwise
    // carry the existing time-of-day onto the new date so we don't
    // silently rewrite the customer's pick.
    const anchor = windows[0]?.start ?? (() => {
      const prev = new Date(window.starts_at);
      const d = new Date(picked);
      d.setHours(prev.getHours(), prev.getMinutes(), 0, 0);
      return d;
    })();
    const starts = anchor.toISOString();
    onChange({
      ...window,
      starts_at: starts,
      ends_at: addMinutes(starts, duration),
    });
  }

  const slotLabel = useMemo(() => {
    if (!todayWindow) return null;
    return todayWindow.start.toLocaleTimeString("en-ZA", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  }, [todayWindow]);

  return (
    <div className="vt-setup vt-setup-single-slot">
      <ClosedDayBanner serviceId={service.id} dateIso={date.slice(0, 10)} />
      {hoursUnconfigured && (
        <div className="vt-hours-pill vt-hours-pill-closed" role="status">
          <span className="vt-hours-pill-dot" aria-hidden="true" />
          Set operating hours to use the single-slot layout — the time is
          resolved from your daily open window.
        </div>
      )}
      <div className={`vt-setup-grid${ctaSlot ? " vt-setup-grid--with-cta" : ""}`}>
        <DayNavField
          label={fieldLabel(service, "when", "Date")}
          type="date"
          value={date}
          minDay={todayDateOnly()}
          serviceId={service.id}
          onChange={setDate}
        />
        <div className="vt-setup-field">
          <label>Time</label>
          <div
            className="vt-single-slot-time"
            aria-label={
              slotLabel ? `Time locked at ${slotLabel}` : "No time available"
            }
          >
            {slotLabel ?? "—"}
          </div>
        </div>
        {!hidePartyStepper && (
          <PartyStepper window={window} onChange={onChange} service={service} />
        )}
        {ctaSlot && <div className="vt-setup-cta">{ctaSlot}</div>}
      </div>
      {slotLabel ? (
        <p className="vt-muted vt-single-slot-hint">
          One {service.service_type} session per day —{" "}
          <strong>{slotLabel}</strong>. Pick a date and we&apos;ll lock the
          slot for you.
        </p>
      ) : (
        !hoursUnconfigured && (
          <p className="vt-muted vt-single-slot-hint">
            Closed on this date — try another.
          </p>
        )
      )}
    </div>
  );
}

// ─── Time list (date input + vertical time-chip list) ────────────────────

function TimeListSetup({
  service,
  window,
  onChange,
  busyIntervals,
  serviceHours,
  ctaSlot,
  hidePartyStepper = false,
}: {
  service: WidgetService;
  window: BookingWindow;
  onChange: (w: BookingWindow) => void;
  busyIntervals: readonly BusyInterval[];
  serviceHours: readonly ServiceHoursRow[];
  ctaSlot?: ReactNode;
  /** A special date's own picker owns the party (see DatetimeSetup). */
  hidePartyStepper?: boolean;
}) {
  const duration = minutesBetween(window.starts_at, window.ends_at);
  const date = useMemo(() => toDateOnly(window.starts_at), [window.starts_at]);
  const slots = useDailySlots(serviceHours, window.starts_at, 30);

  function setDate(v: string) {
    const picked = new Date(`${v}T00:00:00`);
    const windows = windowsForDate(serviceHours, picked);
    const anchor = windows[0]?.start ?? (() => {
      // No open window for the new date — carry the existing
      // time-of-day forward so we don't silently shift it. The
      // SlotList renders an explicit empty-state in this case.
      const prev = new Date(window.starts_at);
      const d = new Date(picked);
      d.setHours(prev.getHours(), prev.getMinutes(), 0, 0);
      return d;
    })();
    const starts = anchor.toISOString();
    onChange({
      ...window,
      starts_at: starts,
      ends_at: addMinutes(starts, duration),
    });
  }

  function pickSlot(iso: string) {
    onChange({
      ...window,
      starts_at: iso,
      ends_at: addMinutes(iso, duration),
    });
  }

  return (
    <div className="vt-setup vt-setup-time-list">
      <ClosedDayBanner serviceId={service.id} dateIso={date.slice(0, 10)} />
      <div className="vt-setup-grid">
        <DayNavField
          label={fieldLabel(service, "when", "Date")}
          type="date"
          value={date}
          minDay={todayDateOnly()}
          serviceId={service.id}
          onChange={setDate}
        />
        {!hidePartyStepper && (
          <PartyStepper window={window} onChange={onChange} service={service} />
        )}
        {/* 2 fields + CTA fills the grid's natural third column — no
            --with-cta modifier needed here. */}
        {ctaSlot && <div className="vt-setup-cta">{ctaSlot}</div>}
      </div>
      <SlotList
        result={slots}
        window={window}
        duration={duration}
        busyIntervals={busyIntervals}
        onPick={pickSlot}
      />
    </div>
  );
}

function SlotList({
  result,
  window,
  duration,
  busyIntervals,
  onPick,
  layout = "list",
}: {
  result: SlotResult;
  window: BookingWindow;
  duration: number;
  busyIntervals: readonly BusyInterval[];
  onPick: (iso: string) => void;
  // 'list' = grid of buttons (TimeListSetup); 'chips' = wrap row used by
  // CalendarFirstSetup. Empty-state copy is identical across both.
  layout?: "list" | "chips";
}) {
  if (result.kind === "unconfigured") {
    return (
      <p className="vt-muted vt-time-list-empty">
        Times can&apos;t show until the tenant sets operating hours for this
        service. Switch the booking layout to “Date &amp; time input” for a
        free-form picker, or ask staff to configure hours.
      </p>
    );
  }
  if (result.kind === "closed_today" || result.slots.length === 0) {
    return (
      <p className="vt-muted vt-time-list-empty">
        No times for this date — try another.
      </p>
    );
  }
  if (layout === "chips") {
    return (
      <div className="vt-time-chips">
        {result.slots.map((slot) => {
          const slotMs = Date.parse(slot);
          const endMs = slotMs + duration * 60000;
          const conflict = findOverlap(busyIntervals, slotMs, endMs);
          const active = slot === window.starts_at;
          return (
            <button
              key={slot}
              type="button"
              onClick={() => onPick(slot)}
              disabled={Boolean(conflict)}
              title={conflict ? `Overlaps with ${conflict.description}` : undefined}
              className={`vt-chip${active ? " vt-chip-active" : ""}${conflict ? " vt-chip-busy" : ""}`}
            >
              {new Date(slot).toLocaleTimeString("en-ZA", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              })}
            </button>
          );
        })}
      </div>
    );
  }
  return (
    <ul className="vt-time-list" role="listbox" aria-label="Available times">
      {result.slots.map((slot) => {
        const slotMs = Date.parse(slot);
        const endMs = slotMs + duration * 60000;
        const conflict = findOverlap(busyIntervals, slotMs, endMs);
        const active = slot === window.starts_at;
        return (
          <li key={slot}>
            <button
              type="button"
              role="option"
              aria-selected={active}
              className={`vt-time-list-item${active ? " vt-time-list-item-active" : ""}${conflict ? " vt-time-list-item-busy" : ""}`}
              onClick={() => onPick(slot)}
              disabled={Boolean(conflict)}
              title={conflict ? `Overlaps with ${conflict.description}` : undefined}
            >
              <span className="vt-time-list-time">
                {new Date(slot).toLocaleTimeString("en-ZA", {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,
                })}
              </span>
              {conflict && <span className="vt-time-list-note">in booking</span>}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

// ─── Calendar first (month grid → time chips for the picked date) ────────

function CalendarFirstSetup({
  service,
  window,
  onChange,
  busyIntervals,
  serviceHours,
  hidePartyStepper = false,
}: {
  service: WidgetService;
  window: BookingWindow;
  onChange: (w: BookingWindow) => void;
  busyIntervals: readonly BusyInterval[];
  serviceHours: readonly ServiceHoursRow[];
  /** A special date's own picker owns the party (see DatetimeSetup). */
  hidePartyStepper?: boolean;
}) {
  // Closed days grey out here exactly as in the popover calendar.
  const calClosedDates = useClosedDates(service.id);
  const picked = useMemo(() => new Date(window.starts_at), [window.starts_at]);
  // The visible month starts at the picked date's month; navigation
  // bumps a local offset relative to "today's month".
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const monthStart = useMemo(() => {
    const d = new Date(picked);
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [picked]);
  const monthGrid = useMemo(() => buildMonthGrid(monthStart), [monthStart]);

  const duration = minutesBetween(window.starts_at, window.ends_at);
  const slots = useDailySlots(serviceHours, window.starts_at, 30);

  function pickDate(date: Date) {
    const windows = windowsForDate(serviceHours, date);
    const anchor = windows[0]?.start ?? (() => {
      // Same rule as TimeListSetup — preserve the existing time
      // when the new date has no open window, rather than guessing.
      const prev = new Date(window.starts_at);
      const d = new Date(date);
      d.setHours(prev.getHours(), prev.getMinutes(), 0, 0);
      return d;
    })();
    const starts = anchor.toISOString();
    onChange({
      ...window,
      starts_at: starts,
      ends_at: addMinutes(starts, duration),
    });
  }

  function pickSlot(iso: string) {
    onChange({
      ...window,
      starts_at: iso,
      ends_at: addMinutes(iso, duration),
    });
  }

  function shiftMonth(delta: 1 | -1) {
    const next = new Date(monthStart);
    next.setMonth(next.getMonth() + delta);
    // Carry the same day-of-month if possible.
    const day = Math.min(picked.getDate(), daysInMonth(next));
    next.setDate(day);
    pickDate(next);
  }

  return (
    <div className="vt-setup vt-setup-calendar-first">
      <div className="vt-cal-head">
        <button
          type="button"
          className="vt-cal-nav"
          onClick={() => shiftMonth(-1)}
          aria-label="Previous month"
        >
          ‹
        </button>
        <div className="vt-cal-title">
          {monthStart.toLocaleString("en-ZA", { month: "long", year: "numeric" })}
        </div>
        <button
          type="button"
          className="vt-cal-nav"
          onClick={() => shiftMonth(1)}
          aria-label="Next month"
        >
          ›
        </button>
      </div>
      <div className="vt-cal-dow">
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
          <span key={i}>{d}</span>
        ))}
      </div>
      <div className="vt-cal-grid">
        {monthGrid.map((day, idx) => {
          if (!day) return <span key={idx} className="vt-cal-cell vt-cal-cell-empty" />;
          const inPast = day < today;
          const dayKey = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, "0")}-${String(day.getDate()).padStart(2, "0")}`;
          const dayClosed = calClosedDates.has(dayKey);
          const isPicked =
            day.getFullYear() === picked.getFullYear() &&
            day.getMonth() === picked.getMonth() &&
            day.getDate() === picked.getDate();
          const isToday =
            day.getFullYear() === today.getFullYear() &&
            day.getMonth() === today.getMonth() &&
            day.getDate() === today.getDate();
          return (
            <button
              key={idx}
              type="button"
              disabled={inPast || dayClosed}
              title={dayClosed ? "The venue is closed on this date" : undefined}
              onClick={() => pickDate(day)}
              className={`vt-cal-cell${isPicked ? " vt-cal-cell-active" : ""}${isToday ? " vt-cal-cell-today" : ""}${inPast ? " vt-cal-cell-past" : ""}${dayClosed ? " vt-cal-cell-closed" : ""}`}
            >
              {day.getDate()}
            </button>
          );
        })}
      </div>
      <div className="vt-cal-foot">
        {!hidePartyStepper && (
          <PartyStepper window={window} onChange={onChange} service={service} />
        )}
      </div>
      <div className="vt-cal-time-list-wrap">
        <div className="vt-section-label">
          Times for{" "}
          {picked.toLocaleDateString("en-ZA", {
            weekday: "short",
            day: "numeric",
            month: "short",
          })}
        </div>
        <SlotList
          result={slots}
          window={window}
          duration={duration}
          busyIntervals={busyIntervals}
          onPick={pickSlot}
          layout="chips"
        />
      </div>
    </div>
  );
}

// ─── Calendar / slot helpers ─────────────────────────────────────────────

function buildMonthGrid(monthStart: Date): (Date | null)[] {
  // Sunday-first 6-row grid. Leading nulls pad to the right day-of-week
  // for the 1st, trailing nulls fill out the last row.
  const grid: (Date | null)[] = [];
  const firstDow = monthStart.getDay(); // 0 = Sun
  for (let i = 0; i < firstDow; i++) grid.push(null);
  const days = daysInMonth(monthStart);
  for (let d = 1; d <= days; d++) {
    const date = new Date(monthStart);
    date.setDate(d);
    grid.push(date);
  }
  while (grid.length % 7 !== 0) grid.push(null);
  return grid;
}

function daysInMonth(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
}

// Slot kind returned to chip-based layouts. `unconfigured` is distinct
// from "closed today" so the widget can show a tenant-actionable hint
// ("set operating hours") rather than misleading the customer with
// "no times today". The Date-only "closed today" cases stay as an
// empty list with no flag — the day-of-week row exists but is closed.
type SlotResult =
  | { kind: "ok"; slots: string[] }
  | { kind: "closed_today" }
  | { kind: "unconfigured" };

// Synthesises slot ISO strings every `intervalMin` minutes across all
// open windows for the date encoded in `anchorIso`. Returns
// `unconfigured` when the service has no operating-hours rows at all
// — the tenant needs to fill those in before chip-based layouts can
// show anything sensible. Returns `closed_today` when hours exist but
// the picked day has no open window.
function useDailySlots(
  serviceHours: readonly ServiceHoursRow[],
  anchorIso: string,
  intervalMin: number,
): SlotResult {
  return useMemo(() => {
    const anchor = new Date(anchorIso);
    if (Number.isNaN(anchor.getTime())) return { kind: "closed_today" };
    if (serviceHours.length === 0) return { kind: "unconfigured" };
    const windows = windowsForDate(serviceHours, anchor);
    if (windows.length === 0) return { kind: "closed_today" };
    const collected: string[] = [];
    for (const w of windows) {
      const cursor = new Date(w.start);
      while (cursor.getTime() <= w.end.getTime()) {
        collected.push(cursor.toISOString());
        cursor.setMinutes(cursor.getMinutes() + intervalMin);
      }
    }
    return { kind: "ok", slots: collected };
  }, [serviceHours, anchorIso, intervalMin]);
}

// ─── Accommodation (check-in + check-out dates) ───────────────────────────

function AccommodationSetup({
  window,
  onChange,
  fixedNights = null,
  minNights = null,
  ctaSlot,
  maxDate,
  serviceId,
  hidePartyStepper = false,
}: {
  window: BookingWindow;
  onChange: (w: BookingWindow) => void;
  /** Resolves the venue's closed days for the check-in / check-out pickers. */
  serviceId?: string;
  fixedNights?: number | null;
  /**
   * Accommodation only: the SHORTEST stay a package will accept, without
   * pinning the length. "Stay & Save 25%, minimum 2 nights" means two nights
   * or more — treating that as a fixed length blocks the three-night stay the
   * offer exists to sell.
   */
  minNights?: number | null;
  ctaSlot?: ReactNode;
  /** check-in upper bound (YYYY-MM-DD) from the service's booking horizon. */
  maxDate?: string;
  /** A special date's own picker owns the party (see DatetimeSetup). */
  hidePartyStepper?: boolean;
}) {
  const startDate = toDateOnly(window.starts_at);
  const endDate = toDateOnly(window.ends_at);
  // A package includes a fixed number of nights — check-out follows check-in.
  const locked = fixedNights != null && fixedNights >= 1;
  // A package with a MINIMUM keeps the stepper; it just cannot go below it.
  const nightsFloor = Math.max(1, minNights ?? 1);

  // The stay's length, in whole nights. Cycling check-in used to keep
  // check-out PINNED, so the ‹ › arrows silently stretched or shrank the stay
  // — the operator's ask: pick a number of nights once, then cycle dates and
  // the whole stay moves together.
  const nights = locked
    ? fixedNights!
    : Math.max(
        1,
        Math.round(
          (new Date(`${endDate}T00:00:00`).getTime() -
            new Date(`${startDate}T00:00:00`).getTime()) /
            86400000,
        ),
      );

  function withNights(checkIn: string, n: number) {
    const starts = new Date(`${checkIn}T15:00:00`);
    const ends = new Date(starts);
    ends.setDate(ends.getDate() + Math.max(1, n));
    ends.setHours(11, 0, 0, 0);
    onChange({ ...window, starts_at: starts.toISOString(), ends_at: ends.toISOString() });
  }

  function setIn(v: string) {
    // Moving check-in PRESERVES the stay length (locked packages already did).
    withNights(v, nights);
  }
  function setOut(v: string) {
    const ends = new Date(`${v}T11:00:00`);
    onChange({ ...window, ends_at: ends.toISOString() });
  }
  function setNights(n: number) {
    withNights(startDate, n);
  }

  return (
    <div className="vt-setup">
      <div
        className={`vt-setup-grid vt-setup-grid--stay${ctaSlot ? " vt-setup-grid--with-cta" : ""}`}
      >
        <DayNavField
          label="Check-in"
          type="date"
          value={startDate}
          inputMax={maxDate}
          minDay={todayDateOnly()}
          maxDay={maxDate}
          serviceId={serviceId}
          onChange={setIn}
        />
        {!locked && (
          <div className="vt-setup-field">
            <label>
              Nights
              {nightsFloor > 1 ? ` · ${nightsFloor} minimum` : ""}
            </label>
            <div className="vt-setup-stepper">
              <button
                type="button"
                onClick={() => setNights(Math.max(nightsFloor, nights - 1))}
                disabled={nights <= nightsFloor}
                aria-label="One night fewer"
              >
                −
              </button>
              <span>{nights}</span>
              <button
                type="button"
                onClick={() => setNights(nights + 1)}
                aria-label="One night more"
              >
                +
              </button>
            </div>
          </div>
        )}
        <DayNavField
          label={
            <>
              Check-out
              {locked ? ` · ${fixedNights} night${fixedNights === 1 ? "" : "s"} included` : ""}
            </>
          }
          type="date"
          value={endDate}
          inputMin={startDate}
          minDay={startDate}
          disabled={locked}
          extraClass={locked ? "vt-setup-field-locked" : ""}
          onChange={setOut}
        />
        {!hidePartyStepper && (
          <PartyStepper window={window} onChange={onChange} label="Guests" />
        )}
        {ctaSlot && <div className="vt-setup-cta">{ctaSlot}</div>}
      </div>
    </div>
  );
}

// ─── Scheduled sessions (dated service units as tabs) ────────────────────
//
// The unit IS the session: each row in service.units carries a
// starts_at/ends_at and renders as a tab. Picking a tab pins the
// window — the customer can't shift the time because the time is
// what makes the session a session. Party stepper lives below.
//
// Groups sessions by date so a 2-day catalogue (Sat / Sun) shows
// "Sat 23 May · 10:00 / 11:00 / 12:00" then "Sun 24 May · 10:00 …".
// Up to 14 days surfaced; older sessions are filtered out by the
// RPC (units with starts_at < now() - 1 day).

function ScheduledSessionsSetup({
  service,
  window,
  onChange,
  busyIntervals,
  hidePartyStepper = false,
}: {
  service: WidgetService;
  window: BookingWindow;
  onChange: (w: BookingWindow) => void;
  busyIntervals: readonly BusyInterval[];
  /** A special date's own picker owns the party (see DatetimeSetup). */
  hidePartyStepper?: boolean;
}) {
  const dated = useMemo(
    () =>
      service.units
        .filter((u): u is WidgetUnit & { starts_at: string; ends_at: string } =>
          Boolean(u.starts_at && u.ends_at),
        )
        .sort(
          (a, b) =>
            Date.parse(a.starts_at) - Date.parse(b.starts_at),
        ),
    [service.units],
  );

  // Group by local-calendar day so the tab row gets a date header
  // every time the day flips.
  const grouped = useMemo(() => {
    const out: Array<{ key: string; date: Date; units: typeof dated }> = [];
    for (const u of dated) {
      const d = new Date(u.starts_at);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      const head = out.find((g) => g.key === key);
      if (head) head.units.push(u);
      else out.push({ key, date: new Date(d.setHours(0, 0, 0, 0)), units: [u] });
    }
    return out;
  }, [dated]);

  function pickUnit(u: WidgetUnit) {
    if (!u.starts_at || !u.ends_at) return;
    onChange({
      ...window,
      starts_at: u.starts_at,
      ends_at: u.ends_at,
    });
  }

  function setStartsMs(ms: number) {
    // Conflict-banner "try X instead" handler. For scheduled sessions
    // we snap to the dated unit whose starts_at matches the suggested
    // ms (if any) — otherwise we drop the change because the customer
    // can't actually book a non-session time.
    const match = dated.find((u) => Date.parse(u.starts_at) === ms);
    if (match) pickUnit(match);
  }

  if (dated.length === 0) {
    return (
      <div className="vt-setup vt-setup-sessions">
        <div className="vt-hours-pill vt-hours-pill-closed" role="status">
          <span className="vt-hours-pill-dot" aria-hidden="true" />
          No scheduled sessions yet — add dated service units under{" "}
          <strong>Service units</strong>, or switch this service to the
          “Date &amp; time input” layout for free-form bookings.
        </div>
        <div className="vt-setup-grid">
          {!hidePartyStepper && (
            <PartyStepper window={window} onChange={onChange} service={service} />
          )}
        </div>
      </div>
    );
  }

  const activeDayLabel = new Date(window.starts_at).toLocaleDateString(
    "en-ZA",
    {
      weekday: "long",
      day: "numeric",
      month: "long",
    },
  );
  const activeTimeLabel = new Date(window.starts_at).toLocaleTimeString(
    "en-ZA",
    {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    },
  );

  return (
    <div className="vt-setup vt-setup-sessions">
      <ConflictBanner
        window={window}
        busyIntervals={busyIntervals}
        onAccept={setStartsMs}
      />
      <div
        className="vt-session-tabs"
        role="tablist"
        aria-label="Available sessions"
      >
        {grouped.map((day) => (
          <div key={day.key} className="vt-session-day">
            <div className="vt-session-day-label">
              {day.date.toLocaleDateString("en-ZA", {
                weekday: "short",
                day: "numeric",
                month: "short",
              })}
            </div>
            <div className="vt-session-day-tabs">
              {day.units.map((u) => {
                const active = u.starts_at === window.starts_at;
                const slotMs = Date.parse(u.starts_at);
                const endMs = Date.parse(u.ends_at);
                const conflict = findOverlap(busyIntervals, slotMs, endMs);
                const sold = u.inventory_count === 0;
                const disabled = sold;
                return (
                  <button
                    key={u.id}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => pickUnit(u)}
                    disabled={disabled}
                    className={`vt-session-tab${active ? " vt-session-tab-active" : ""}${conflict ? " vt-session-tab-busy" : ""}`}
                    title={
                      sold
                        ? "Sold out"
                        : conflict
                          ? `Overlaps with ${conflict.description}`
                          : undefined
                    }
                  >
                    <span className="vt-session-tab-time">
                      {new Date(u.starts_at).toLocaleTimeString("en-ZA", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false,
                      })}
                    </span>
                    <span className="vt-session-tab-name">{u.name}</span>
                    {sold && (
                      <span className="vt-session-tab-note">Sold out</span>
                    )}
                    {!sold && conflict && (
                      <span className="vt-session-tab-note">in booking</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <div className="vt-session-summary">
        <div>
          <div className="vt-section-label">Selected session</div>
          <div className="vt-session-summary-line">
            <strong>{activeDayLabel}</strong> · {activeTimeLabel}
          </div>
        </div>
        {!hidePartyStepper && (
          <PartyStepper window={window} onChange={onChange} service={service} />
        )}
      </div>
    </div>
  );
}

// Picks the first dated unit whose start is ≥ now. Falls back to the
// earliest dated unit if everything is in the past (defensive — the
// RPC already filters to `>= now() - 1 day`, but the cushion means a
// just-passed session can still seed without breaking the layout).
function firstUpcomingDatedUnit(units: WidgetUnit[]): WidgetUnit | null {
  const dated = units.filter((u) => u.starts_at && u.ends_at);
  if (dated.length === 0) return null;
  const sorted = [...dated].sort(
    (a, b) =>
      Date.parse(a.starts_at as string) - Date.parse(b.starts_at as string),
  );
  const nowMs = Date.now();
  return (
    sorted.find((u) => Date.parse(u.starts_at as string) >= nowMs) ??
    sorted[0] ??
    null
  );
}

// ─── Events (read-only window driven by the event unit) ────────────────────

function EventsSetup({
  service,
  window,
  selectedEventUnit,
  onChange,
  busyIntervals,
  ctaSlot,
  hidePartyStepper = false,
}: {
  service: WidgetService;
  window: BookingWindow;
  selectedEventUnit: WidgetUnit | null;
  onChange: (w: BookingWindow) => void;
  busyIntervals: readonly BusyInterval[];
  ctaSlot?: ReactNode;
  /** A special date's own picker owns the party (see DatetimeSetup). */
  hidePartyStepper?: boolean;
}) {
  const dated = service.units.filter((u) => u.starts_at);
  function pickUnit(id: string) {
    const u = dated.find((x) => x.id === id);
    if (!u?.starts_at) return;
    onChange({
      starts_at: u.starts_at,
      ends_at: u.ends_at ?? addMinutes(u.starts_at, 120),
      party_size: window.party_size,
    });
  }

  return (
    <div className="vt-setup">
      {/* Event windows are immutable per-unit, so when there's an
          overlap we just warn — the customer needs to pick a
          different event (no "snap to free slot" since the slots
          are scheduled, not freeform). */}
      <ConflictBanner
        window={window}
        busyIntervals={busyIntervals}
        onAccept={() => {
          /* no-op: events have fixed start/end */
        }}
      />
      <div className="vt-setup-grid">
        <div className="vt-setup-field vt-setup-field-wide">
          <label>Event</label>
          <select
            value={selectedEventUnit?.id ?? ""}
            onChange={(e) => pickUnit(e.target.value)}
          >
            <option value="">Pick an event…</option>
            {dated.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
                {u.starts_at
                  ? ` · ${new Date(u.starts_at).toLocaleString("en-ZA", {
                      weekday: "short",
                      day: "numeric",
                      month: "short",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: false,
                    })}`
                  : ""}
              </option>
            ))}
          </select>
        </div>
        {!hidePartyStepper && (
          <PartyStepper window={window} onChange={onChange} service={service} label="Tickets" />
        )}
        {ctaSlot && <div className="vt-setup-cta">{ctaSlot}</div>}
      </div>
    </div>
  );
}

// ─── Party stepper (reused) ───────────────────────────────────────────────

function PartyStepper({
  window,
  onChange,
  service,
  label,
}: {
  window: BookingWindow;
  onChange: (w: BookingWindow) => void;
  // Optional: layouts that pass an explicit label (e.g. "Guests") don't need it.
  service?: WidgetService;
  // An explicit label (e.g. "Guests", "Tickets") wins; otherwise the tenant's
  // per-service override for the party field, falling back to "Party size".
  label?: string;
}) {
  const resolvedLabel =
    label ??
    (service ? fieldLabel(service, "party_size", "Party size") : "Party size");
  return (
    <div className="vt-setup-field">
      <label>{resolvedLabel}</label>
      <div className="vt-setup-stepper">
        <button
          type="button"
          onClick={() =>
            onChange({ ...window, party_size: Math.max(1, window.party_size - 1) })
          }
          aria-label="Decrease"
        >
          −
        </button>
        <span>{window.party_size}</span>
        <button
          type="button"
          onClick={() =>
            onChange({ ...window, party_size: Math.min(50, window.party_size + 1) })
          }
          aria-label="Increase"
        >
          +
        </button>
      </div>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function roundUpToHalfHour(d: Date): Date {
  const r = new Date(d);
  r.setSeconds(0, 0);
  const m = r.getMinutes();
  const add = m < 30 ? 30 - m : 60 - m;
  r.setMinutes(m + add);
  return r;
}

function minutesBetween(a: string, b: string): number {
  return Math.round((new Date(b).getTime() - new Date(a).getTime()) / 60000);
}

// Advance `from` to the first calendar day whose day-of-week is permitted by
// `allowed` (JS getDay(): 0=Sun..6=Sat — the same convention the combo
// eligibility RPC uses via Postgres extract(dow)). No-op when the set is empty
// or already covers the whole week. Bounded to 7 iterations.
function firstAllowedDay(from: Date, allowed?: readonly number[] | null): Date {
  if (!allowed || allowed.length === 0 || allowed.length >= 7) return from;
  const set = new Set(allowed);
  const d = new Date(from);
  for (let i = 0; i < 7 && !set.has(d.getDay()); i++) {
    d.setDate(d.getDate() + 1);
  }
  return d;
}

function toDateOnly(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Today as a local YYYY-MM-DD — the floor for the day-nav toggles (no past days).
function todayDateOnly(): string {
  return toDateOnly(new Date().toISOString());
}

// Shift the DATE part of a local input value by `delta` days, keeping any time
// part. UTC calendar math on the date → DST/tz-safe (these are plain calendar
// dates, not ChiDesk fake-Z stamps).
function shiftLocalDay(value: string, delta: number): string {
  const datePart = value.slice(0, 10);
  const timePart = value.length > 10 ? value.slice(10) : ""; // "THH:MM"
  const parts = datePart.split("-");
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  const d = Number(parts[2]);
  const shifted = new Date(Date.UTC(y, m - 1, d + delta)).toISOString().slice(0, 10);
  return shifted + timePart;
}

// A .vt-setup-field date/datetime input with prev/next-day chevrons at its right
// edge — the same quick day-stepping as the spa DaySelector, for the other tabs.
// Chevrons shift the DATE (keeping the time for datetime-local), bounded by
// [minDay, maxDay]; the native picker indicator stays inside the input.

// ─── Closed days ──────────────────────────────────────────────────────────
// availability_rules has expressed closed days since April and every hold path
// enforces them — but nothing told the booking page, so a guest picked a closed
// day, filled the form, and learned about it from a rejection.
//
// Shared: DayNavField renders inside several sibling setup components, and
// every one reads the same cached per-service answer (state/booking-window.ts).
// A failed lookup must never block the picker: "no closed days" is the safe
// answer, because the server gate still refuses a closed day at hold time.
function useClosedDates(serviceId: string | undefined): Set<string> {
  return useServiceBookingWindow(serviceId).closedDates;
}

/**
 * Month-grid picker that can actually GREY OUT closed days — the native date
 * input cannot disable individual dates, which is how a guest ended up picking
 * a closed day and reading about it afterwards. Mirrors CalendarFirstSetup's
 * markup and .vt-cal-* styling so the two calendars read as one control.
 */
/** The closed-day notice, in the SAME style and position as every other
 *  setup notice (.vt-conflict-banner) — never squeezed into the field. */
function ClosedDayBanner({
  serviceId,
  dateIso,
}: {
  serviceId: string;
  dateIso: string; // YYYY-MM-DD
}) {
  const closed = useClosedDates(serviceId);
  if (!closed.has(dateIso)) return null;
  return (
    <div className="vt-conflict-banner" role="alert">
      <span className="vt-conflict-banner-text">
        ⚠ The venue is closed on this date — please pick another day.
      </span>
    </div>
  );
}

function DayGridPopover({
  value,
  minDay,
  maxDay,
  closedDates,
  onPick,
  onClose,
  timeValue = null,
  onTimeChange,
  anchorEl,
}: {
  value: string; // YYYY-MM-DD
  minDay?: string;
  maxDay?: string;
  closedDates: Set<string>;
  onPick: (day: string) => void;
  onClose: () => void;
  /** Non-null makes the popover own the TIME too: picking a day keeps it
   *  open, a time row + Done complete the choice in one surface — otherwise
   *  date-then-time took two separate interactions. */
  timeValue?: string | null;
  onTimeChange?: (t: string) => void;
  /** The trigger input — the fixed-position popover anchors to its rect. */
  anchorEl?: React.RefObject<HTMLInputElement | null>;
}) {
  const initial = new Date(`${value}T00:00:00`);
  const [monthStart, setMonthStart] = useState(() => {
    const d = Number.isNaN(initial.getTime()) ? new Date() : initial;
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  // FIXED positioning, anchored to the trigger. The widget card is
  // overflow:hidden, so an absolutely-positioned popover got CLIPPED exactly
  // when a month needs six week-rows (Aug 2026, Jan 2027) — the foot with the
  // time + Done vanished on those months. Fixed positioning escapes every
  // clipping ancestor; re-measured per month (5- vs 6-row heights differ) and
  // flipped above the field when the viewport runs out below.
  const popRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  useLayoutEffect(() => {
    const pop = popRef.current;
    const anchor = anchorEl?.current;
    if (!pop || !anchor) return;
    const r = anchor.getBoundingClientRect();
    const h = pop.offsetHeight;
    const w = pop.offsetWidth;
    const below = r.bottom + 4;
    const top =
      below + h + 8 <= (window.innerHeight || 0) || r.top - h - 8 < 0
        ? below
        : r.top - h - 4;
    const left = Math.max(8, Math.min(r.left, (window.innerWidth || w) - w - 8));
    setPos({ top, left });
  }, [monthStart, anchorEl]);
  useEffect(() => {
    // Anything that moves the anchor invalidates the position — close rather
    // than chase it.
    const close = () => onClose();
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const grid = buildMonthGrid(monthStart);
  const iso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const shift = (delta: 1 | -1) => {
    setMonthStart((m) => new Date(m.getFullYear(), m.getMonth() + delta, 1));
  };
  return (
    <WidgetLayer>
      {/* click-away backdrop */}
      <div className="vt-daynav-backdrop" onClick={onClose} />
      <div
        ref={popRef}
        className="vt-daynav-pop"
        role="dialog"
        aria-label="Pick a date"
        style={pos ? { top: pos.top, left: pos.left } : { visibility: "hidden" }}
      >
        <div className="vt-cal-head">
          <button type="button" className="vt-cal-nav" aria-label="Previous month"
            onClick={() => shift(-1)}>‹</button>
          <div className="vt-cal-title">
            {monthStart.toLocaleString("en-ZA", { month: "long", year: "numeric" })}
          </div>
          <button type="button" className="vt-cal-nav" aria-label="Next month"
            onClick={() => shift(1)}>›</button>
        </div>
        <div className="vt-cal-dow">
          {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
            <span key={i}>{d}</span>
          ))}
        </div>
        <div className="vt-cal-grid">
          {grid.map((day, idx) => {
            if (!day) return <span key={idx} className="vt-cal-cell vt-cal-cell-empty" />;
            const dayIso = iso(day);
            const closed = closedDates.has(dayIso);
            const outOfRange =
              (minDay ? dayIso < minDay : false) || (maxDay ? dayIso > maxDay : false);
            const isPicked = dayIso === value;
            return (
              <button
                key={idx}
                type="button"
                className={`vt-cal-cell${isPicked ? " vt-cal-cell-active" : ""}${
                  closed ? " vt-cal-cell-closed" : ""
                }${outOfRange ? " vt-cal-cell-past" : ""}`}
                disabled={closed || outOfRange}
                aria-disabled={closed || outOfRange}
                title={closed ? "The venue is closed on this date" : undefined}
                onClick={() => {
                  onPick(dayIso);
                  if (timeValue == null) onClose();
                }}
              >
                {day.getDate()}
              </button>
            );
          })}
        </div>
        {timeValue != null && (
          <div className="vt-cal-foot vt-cal-time">
            <input
              type="time"
              value={timeValue}
              onChange={(e) => {
                if (e.target.value) onTimeChange?.(e.target.value);
              }}
            />
            <button type="button" className="vt-cal-done" onClick={onClose}>
              Done
            </button>
          </div>
        )}
      </div>
    </WidgetLayer>
  );
}

function DayNavField({
  label,
  type,
  value,
  inputMin,
  inputMax,
  minDay,
  maxDay,
  disabled = false,
  extraClass = "",
  serviceId,
  onChange,
}: {
  label: ReactNode;
  type: "date" | "datetime-local";
  value: string; // local input string
  inputMin?: string;
  inputMax?: string;
  minDay?: string; // YYYY-MM-DD toggle floor
  maxDay?: string; // YYYY-MM-DD toggle ceiling
  disabled?: boolean;
  extraClass?: string;
  /** Resolves the venue's closed days. Native date inputs cannot grey
   *  individual days out, so we say it plainly instead — and the arrows step
   *  over them. */
  serviceId?: string;
  onChange: (localValue: string) => void;
}) {
  const closedDates = useClosedDates(serviceId);
  const datePart = value.slice(0, 10);
  const canPrev = !disabled && (!minDay || datePart > minDay);
  const canNext = !disabled && (!maxDay || datePart < maxDay);
  const shift = (delta: number) => {
    // Step OVER closed days rather than landing on one and explaining. Bounded
    // by the same floor/ceiling as a single step, so a long closure cannot spin
    // this past the horizon; if every remaining day is closed we stay put and
    // the notice below says why.
    let next = shiftLocalDay(value, delta);
    for (let guard = 0; guard < 400; guard += 1) {
      const nd = next.slice(0, 10);
      if (minDay && nd < minDay) return;
      if (maxDay && nd > maxDay) return;
      if (!closedDates?.has(nd)) {
        onChange(next);
        return;
      }
      next = shiftLocalDay(next, delta);
    }
  };
  // BOTH variants use the custom grid for the DATE part — the native
  // date/datetime pickers cannot disable individual days, so a closed day
  // stayed selectable in Chrome's own calendar (reported live on the dining
  // layout, which is datetime-local). datetime-local keeps a native input
  // only for the TIME half.
  const [gridOpen, setGridOpen] = useState(false);
  const anchorRef = useRef<HTMLInputElement | null>(null);
  const timePart = type === "datetime-local" ? (value.slice(11, 16) || "12:00") : null;
  const display = (() => {
    const d = new Date(`${datePart}T00:00:00`);
    return Number.isNaN(d.getTime())
      ? datePart
      : d.toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" });
  })();
  return (
    <div className={`vt-setup-field vt-setup-field--daynav${extraClass ? ` ${extraClass}` : ""}`}>
      <label>{label}</label>
      {/* Always classed: the field row is flex and this wrapper must take
          over the grow role the bare input used to have — unclassed it sized
          to content and the leftover width pooled after the arrows. */}
      <div className="vt-daynav-datetime">
        <input
          ref={anchorRef}
          type="text"
          readOnly
          value={display}
          disabled={disabled}
          aria-haspopup="dialog"
          aria-expanded={gridOpen}
          onClick={() => !disabled && setGridOpen((o) => !o)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              if (!disabled) setGridOpen((o) => !o);
            }
          }}
        />
        {timePart != null && (
          <input
            type="time"
            value={timePart}
            disabled={disabled}
            onChange={(e) => {
              if (e.target.value) onChange(`${datePart}T${e.target.value}`);
            }}
          />
        )}
      </div>
      {gridOpen && (
        <DayGridPopover
          value={datePart}
          minDay={minDay}
          maxDay={maxDay}
          closedDates={closedDates ?? new Set()}
          onPick={(d) =>
            onChange(timePart != null ? `${d}T${timePart}` : d)
          }
          onClose={() => setGridOpen(false)}
          timeValue={timePart}
          onTimeChange={(t) => onChange(`${datePart}T${t}`)}
          anchorEl={anchorRef}
        />
      )}
      <div className="vt-setup-daynav">
        <button
          type="button"
          className="vt-day-nav"
          aria-label="Previous day"
          disabled={!canPrev}
          onClick={() => shift(-1)}
        >
          &lsaquo;
        </button>
        <button
          type="button"
          className="vt-day-nav"
          aria-label="Next day"
          disabled={!canNext}
          onClick={() => shift(1)}
        >
          &rsaquo;
        </button>
      </div>
    </div>
  );
}

// The lengths a guest may pick from for a service that has not set one of its
// own. The service's configured duration_minutes is merged in (and selected)
// by durationOptionsFor in @vertu/shared — before that, a 45-minute ride had
// no option matching its own setting, so the picker rendered blank and any
// touch of it resized the booking to 90 minutes or more.
function standardDurationsFor(type: string): number[] {
  switch (type) {
    case "dining":
      return [60, 90, 120, 150, 180];
    case "spa":
      return [30, 60, 90, 120];
    case "tasting":
      return [45, 60, 90];
    case "picnic":
    case "tour":
      return [90, 120, 180, 240];
    default:
      return [60, 90, 120, 180];
  }
}
